import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Stethoscope, BadgeCheck, ArrowLeft, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { api, apiError, imgUrl } from "@/lib/api";
import { todayAST, fmtDate, nowTimeAST } from "@/lib/date";
import PatientNav from "@/components/PatientNav";
import { InsuranceChips } from "@/components/InsurancesEditor";

const FALLBACK =
  "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODc2MTg4MjV8MA&ixlib=rb-4.1.0&q=85";

const fmt = fmtDate;
const today = () => todayAST();

export default function DoctorProfile() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [date, setDate] = useState(today());
  const [viewMonth, setViewMonth] = useState(new Date());
  const [availDays, setAvailDays] = useState(new Set());
  const [blocks, setBlocks] = useState([]);
  const [selBlock, setSelBlock] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/doctors/${doctorId}`).then((r) => setDoctor(r.data));
  }, [doctorId]);

  const loadDays = async (monthDate) => {
    const m = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
    const { data } = await api.get(`/doctors/${doctorId}/available-days`, { params: { month: m } });
    setAvailDays(new Set(data));
  };
  useEffect(() => { loadDays(viewMonth); /* eslint-disable-next-line */ }, [doctorId]);

  const loadSlots = async (d) => {
    setSelBlock(null);
    setSelSlot(null);
    const { data } = await api.get(`/doctors/${doctorId}/slots`, { params: { date: d } });
    // When the selected day is today in AST, keep a block only until its start time passes.
    const filtered = d === todayAST()
      ? data.filter((b) => (b.start_time || "") > nowTimeAST())
      : data;
    setBlocks(filtered);
  };

  useEffect(() => { loadSlots(date); /* eslint-disable-next-line */ }, [date, doctorId]);

  const book = async () => {
    if (!selBlock) return;
    if (selBlock.modality === "HORAS" && !selSlot) {
      toast.error("Selecciona una hora");
      return;
    }
    setBusy(true);
    try {
      await api.post("/appointments", {
        doctor_id: doctorId,
        availability_id: selBlock.id,
        slot_time: selBlock.modality === "HORAS" ? selSlot : null,
        reason,
      });
      toast.success("Cita agendada");
      navigate("/mis-citas");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!doctor) return <div className="min-h-screen bg-slate-50"><PatientNav /></div>;
  const p = doctor.profile || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <PatientNav />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <button onClick={() => navigate(-1)} className="mb-5 flex items-center gap-1.5 text-sm font-bold text-slate-500" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              <img src={imgUrl(doctor.picture || p.photo_url) || FALLBACK} alt={doctor.name} className="h-24 w-24 rounded-full border border-[#E2E8F0] object-cover" onError={(e) => (e.currentTarget.src = FALLBACK)} />
              <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900" data-testid="doctor-name">{doctor.name}</h1>
              <p className="flex items-center gap-1.5 text-sm font-bold text-[#0284C7]"><Stethoscope className="h-4 w-4" /> {p.specialty}</p>
              <div className="mt-4 space-y-2 text-sm font-semibold text-slate-500">
                <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {p.center_name} · {p.location}</div>
                <div className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-emerald-500" /> Licencia {p.license_number}</div>
                <div className="text-lg font-extrabold text-slate-900">RD$ {p.price?.toLocaleString?.() ?? p.price}</div>
              </div>
              <div className="mt-4 border-t border-[#E2E8F0] pt-4" data-testid="doctor-insurances">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Seguros aceptados</div>
                <InsuranceChips items={p.insurances} testid="doctor-insurance-list" />
              </div>
              {p.bio && <p className="mt-4 border-t border-[#E2E8F0] pt-4 text-sm font-semibold leading-relaxed text-slate-500">{p.bio}</p>}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Agendar cita</h2>
              <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Fecha</label>
                <p className="mt-1 text-xs font-semibold text-slate-400">Solo puedes elegir días con disponibilidad del médico.</p>
                <div className="mt-2 inline-block rounded-xl border border-[#E2E8F0] p-2" data-testid="booking-calendar">
                  <Calendar
                    mode="single"
                    selected={new Date(date + "T00:00:00")}
                    onSelect={(d) => d && setDate(fmt(d))}
                    month={viewMonth}
                    onMonthChange={(m) => { setViewMonth(m); loadDays(m); }}
                    disabled={(d) => fmt(d) < todayAST() || !availDays.has(fmt(d))}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Motivo de la cita</label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe brevemente el motivo de tu consulta…"
                  className="mt-1.5 min-h-20 rounded-xl border-[#E2E8F0]"
                  data-testid="reason-input"
                />
              </div>

              <div className="mt-6 space-y-4">
                {blocks.length === 0 && (
                  <p className="rounded-xl border border-dashed border-[#E2E8F0] p-6 text-center text-sm font-bold text-slate-400" data-testid="no-slots">
                    Sin disponibilidad para esta fecha.
                  </p>
                )}
                {blocks.map((b) => (
                  <div key={b.id} className={`rounded-xl border p-4 transition-colors duration-200 ${selBlock?.id === b.id ? "border-[#0284C7] bg-sky-50/40" : "border-[#E2E8F0]"}`} data-testid={`block-${b.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        {b.modality === "HORAS" ? <Clock className="h-4 w-4 text-[#0284C7]" /> : <Users className="h-4 w-4 text-[#0284C7]" />}
                        {b.start_time}–{b.end_time} · {b.center_name}
                      </div>
                      <span className="rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-bold text-slate-500">
                        {b.modality === "HORAS" ? "Hora fija" : `${b.remaining_cupos} cupos`}
                      </span>
                    </div>

                    {b.modality === "HORAS" ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(b.available_slots || []).length === 0 && <span className="text-xs font-bold text-slate-400">Sin horas libres</span>}
                        {(b.available_slots || []).map((s) => (
                          <button
                            key={s}
                            onClick={() => { setSelBlock(b); setSelSlot(s); }}
                            data-testid={`slot-${b.id}-${s}`}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${selBlock?.id === b.id && selSlot === s ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600 hover:border-[#0284C7]"}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <button
                        onClick={() => { setSelBlock(b); setSelSlot(null); }}
                        disabled={b.remaining_cupos <= 0}
                        data-testid={`cupo-select-${b.id}`}
                        className={`mt-3 rounded-full border px-4 py-1.5 text-xs font-bold disabled:opacity-40 ${selBlock?.id === b.id ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600"}`}
                      >
                        {selBlock?.id === b.id ? "Seleccionado" : "Tomar cupo por llegada"}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <Button onClick={book} disabled={busy || !selBlock} className="mt-6 w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="confirm-booking-btn">
                {busy ? "Agendando…" : "Confirmar cita"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
